import { inject } from '@angular/core';
import { CanActivateFn, CanDeactivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from 'shared-lib';
import { ForgetPasswordService } from './forget-password.service';

export const forgetPasswordOtpGuard: CanActivateFn = () => {
  const forgetPasswordService = inject(ForgetPasswordService);
  const router = inject(Router);

  if (forgetPasswordService.hasOtpState()) {
    return true;
  }

  forgetPasswordService.reset();
  return router.createUrlTree(['/auth/forget-password/mobile-input']);
};

export const forgetPasswordSetPasswordGuard: CanActivateFn = () => {
  const forgetPasswordService = inject(ForgetPasswordService);
  const router = inject(Router);

  if (forgetPasswordService.hasPasswordState()) {
    return true;
  }

  forgetPasswordService.reset();
  return router.createUrlTree(['/auth/forget-password/mobile-input']);
};

export const forgetPasswordOtpCanDeactivateGuard: CanDeactivateFn<unknown> = (_, __, ___, nextState) => {
  const forgetPasswordService = inject(ForgetPasswordService);

  if (nextState?.url?.includes('/auth/forget-password/set-password')) {
    return true;
  }

  forgetPasswordService.userDetails.update((curr) => {
    if (!curr) {
      return curr;
    }
    return {
      ...curr,
      otpValidated: false,
      otp: undefined,
      otpSessionId: undefined,
    };
  });

  return true;
};

// Guards against the confirmation dialog being triggered a second time for the same logical
// "discard progress" action. Confirming the dialog calls forgetPasswordService.reset(), which
// makes forgetPasswordOtpGuard's hasOtpState() check fail and redirect to the mobile-input step.
// That redirect cancels the in-flight navigation and starts a new one still leaving the
// (still-active) set-password route, so the Router re-invokes this canDeactivate guard
// immediately afterwards. discardConfirmed lets that follow-up invocation through silently.
let pendingDiscardConfirmation: Promise<boolean> | null = null;
let discardConfirmed = false;

export const forgetPasswordSetPasswordCanDeactivateGuard: CanDeactivateFn<unknown> = () => {
  const forgetPasswordService = inject(ForgetPasswordService);
  const router = inject(Router);
  const utils = inject(UtilsService);
  const translate = inject(TranslateService);
  const navigation = router.getCurrentNavigation();

  if (navigation?.extras?.state?.['forgetPasswordComplete']) {
    forgetPasswordService.reset();
    return true;
  }

  if (discardConfirmed) {
    discardConfirmed = false;
    return true;
  }

  if (pendingDiscardConfirmation) {
    return pendingDiscardConfirmation;
  }

  pendingDiscardConfirmation = new Promise<boolean>((resolve) => {
    utils.getConfirmation(
      translate.instant('auth.forgetPassword.discardProgressTitle'),
      translate.instant('auth.forgetPassword.discardProgressMessage'),
      (confirmed) => {
        if (confirmed) {
          discardConfirmed = true;
          forgetPasswordService.reset();
          resolve(true);
          return;
        }
        resolve(false);
      }
    );
  }).finally(() => {
    pendingDiscardConfirmation = null;
  });

  return pendingDiscardConfirmation;
};
